import React, { useState, useEffect } from 'react';
import { Info, ChevronDown, ChevronUp, Plus, X, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * BridgeBuilderSection - Section 6: Bridge Sentences
 *
 * Structure:
 * - Minimum 2 bridges, maximum 4
 * - Each bridge: School Element + My Experience + Bridge Sentence
 *
 * Bridge Data Model:
 * {
 *   school_element: string (140 chars),
 *   my_experience: string (160 chars),
 *   bridge_sentence: string (200 chars),
 *   ai_options_shown: string[],
 *   student_selected: number,
 *   student_edited: boolean
 * }
 */
export default function BridgeBuilderSection({ data, onChange, collegeData, allSectionData }) {
  const [showGuidance, setShowGuidance] = useState(false);
  const [generatingForIndex, setGeneratingForIndex] = useState(null);

  // Auto-populate bridges from previous sections on first render
  useEffect(() => {
    if (!data.rows || data.rows.length === 0) {
      const starterBridges = [];

      // From academics - courses
      if (allSectionData?.academics?.courses && allSectionData.academics.courses.length > 0) {
        const course = allSectionData.academics.courses[0];
        if (course.title) {
          starterBridges.push({
            school_element: course.title,
            my_experience: allSectionData?.personal?.micro_moment || '',
            bridge_sentence: '',
            ai_options_shown: [],
            student_selected: null,
            student_edited: false
          });
        }
      }

      // From academics - professors
      if (allSectionData?.academics?.professors && allSectionData.academics.professors.length > 0 && starterBridges.length < 2) {
        const prof = allSectionData.academics.professors[0];
        if (prof.name) {
          starterBridges.push({
            school_element: `${prof.name}'s research`,
            my_experience: allSectionData?.personal?.micro_moment || '',
            bridge_sentence: '',
            ai_options_shown: [],
            student_selected: null,
            student_edited: false
          });
        }
      }

      // From academics - initiatives
      if (allSectionData?.academics?.initiatives && allSectionData.academics.initiatives.length > 0 && starterBridges.length < 2) {
        const init = allSectionData.academics.initiatives[0];
        if (init.name) {
          starterBridges.push({
            school_element: init.name,
            my_experience: allSectionData?.personal?.micro_moment || '',
            bridge_sentence: '',
            ai_options_shown: [],
            student_selected: null,
            student_edited: false
          });
        }
      }

      // From campus - clubs
      if (allSectionData?.campus?.clubs && allSectionData.campus.clubs.length > 0 && starterBridges.length < 3) {
        const club = allSectionData.campus.clubs[0];
        if (club.name) {
          starterBridges.push({
            school_element: club.name,
            my_experience: allSectionData?.personal?.motivations?.[0] || '',
            bridge_sentence: '',
            ai_options_shown: [],
            student_selected: null,
            student_edited: false
          });
        }
      }

      // Ensure we have at least 2 bridges (the minimum)
      while (starterBridges.length < 2) {
        starterBridges.push({
          school_element: '',
          my_experience: '',
          bridge_sentence: '',
          ai_options_shown: [],
          student_selected: null,
          student_edited: false
        });
      }

      if (starterBridges.length > 0) {
        onChange({ ...data, rows: starterBridges });
      }
    }
  }, []); // Only run on mount

  // === BRIDGES ===
  const addBridge = () => {
    const bridges = data.rows || [];
    if (bridges.length >= 4) {
      toast.error('Maximum 4 bridges allowed');
      return;
    }
    onChange({
      ...data,
      rows: [...bridges, {
        school_element: '',
        my_experience: '',
        bridge_sentence: '',
        ai_options_shown: [],
        student_selected: null,
        student_edited: false
      }]
    });
  };

  const updateBridge = (index, field, value) => {
    const bridges = [...(data.rows || [])];
    bridges[index] = { ...bridges[index], [field]: value };

    // If student edits bridge_sentence manually, mark as edited
    if (field === 'bridge_sentence' && bridges[index].ai_options_shown?.length > 0) {
      bridges[index].student_edited = true;
    }

    onChange({ ...data, rows: bridges });
  };

  const removeBridge = (index) => {
    const bridges = [...(data.rows || [])];
    bridges.splice(index, 1);
    onChange({ ...data, rows: bridges });
  };

  // Generate AI bridge suggestions
  const generateBridges = async (index) => {
    const bridge = (data.rows || [])[index];

    if (!bridge.school_element || !bridge.my_experience) {
      toast.error('Please fill in school element and your experience first');
      return;
    }

    setGeneratingForIndex(index);

    try {
      const response = await fetch('/api/why-school/generate-bridges', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_element: bridge.school_element,
          my_experience: bridge.my_experience,
          school_context: {
            ethos: allSectionData?.ethos || {},
            academics: allSectionData?.academics || {},
            campus: allSectionData?.campus || {},
            personal: allSectionData?.personal || {}
          },
          college_name: collegeData?.global?.name || 'this school'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate bridges');
      }

      const result = await response.json();

      // Update bridge with AI options
      const bridges = [...(data.rows || [])];
      bridges[index] = {
        ...bridges[index],
        ai_options_shown: result.bridge_options || [],
        bridge_sentence: result.bridge_options?.[0] || '',
        student_selected: 0,
        student_edited: false
      };
      onChange({ ...data, rows: bridges });

      toast.success('Bridge options generated! Choose one or edit to your liking.');
    } catch (err) {
      console.error('Error generating bridges:', err);
      toast.error('Could not generate bridge suggestions. Try writing your own!');
    } finally {
      setGeneratingForIndex(null);
    }
  };

  // Select an AI option
  const selectAIOption = (bridgeIndex, optionIndex) => {
    const bridges = [...(data.rows || [])];
    bridges[bridgeIndex] = {
      ...bridges[bridgeIndex],
      bridge_sentence: bridges[bridgeIndex].ai_options_shown[optionIndex],
      student_selected: optionIndex,
      student_edited: false
    };
    onChange({ ...data, rows: bridges });
  };

  return (
    <div className="space-y-8">
      {/* Section Guidance */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <button
          onClick={() => setShowGuidance(!showGuidance)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center space-x-2">
            <Info className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-indigo-900">Section Guidance</span>
          </div>
          {showGuidance ? (
            <ChevronUp className="w-5 h-5 text-indigo-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-indigo-600" />
          )}
        </button>

        {showGuidance && (
          <div className="mt-4 space-y-3 text-sm text-indigo-800">
            <p className="font-medium">What This Section Does:</p>
            <p>
              Bridge sentences connect your personal experiences to specific school resources.
              They show <em>how</em> you'll engage with the school, not just that you want to attend.
            </p>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Strong Bridge Example:</p>
              <p className="text-xs text-gray-600 mb-1">School Element: MIT D-Lab</p>
              <p className="text-xs text-gray-600 mb-2">My Experience: Built air-quality sensors after community interviews</p>
              <p className="italic">
                "Because D-Lab starts with empathy before engineering, I'd test whether my
                'community-first' approach scales when the problem isn't in my own backyard."
              </p>
            </div>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Weak Bridge Example:</p>
              <p className="line-through italic">
                "I'm excited to take advantage of D-Lab to learn more about design thinking."
              </p>
              <p className="text-red-700 text-xs mt-1">
                ❌ Generic—doesn't show HOW you'll engage or what question you'll explore
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bridges Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Bridge Sentences (2-4)</h3>
          <p className="text-sm text-gray-600 mt-1">
            Connect your experiences to specific school resources
          </p>
        </div>
        <button
          onClick={addBridge}
          disabled={(data.rows || []).length >= 4}
          className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          <span>Add Bridge</span>
        </button>
      </div>

      {/* Bridges List */}
      <div className="space-y-6">
        {(data.rows || []).map((bridge, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <h4 className="text-sm font-bold text-gray-900">Bridge #{index + 1}</h4>
              <button
                onClick={() => removeBridge(index)}
                className="p-1 text-gray-400 hover:text-red-600 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Field 1: School Element */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  School Element
                </label>
                <input
                  type="text"
                  value={bridge.school_element || ''}
                  onChange={(e) => updateBridge(index, 'school_element', e.target.value)}
                  placeholder="e.g., MIT D-Lab, Professor Jane Smith's research, Code for Good club"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Field 2: My Experience */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  My Related Experience
                </label>
                <textarea
                  value={bridge.my_experience || ''}
                  onChange={(e) => updateBridge(index, 'my_experience', e.target.value)}
                  placeholder="Briefly describe your experience that connects to this school element"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[60px]"
                  style={{ height: 'auto', minHeight: '60px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                />
              </div>

              {/* Generate Bridge Button */}
              {!bridge.ai_options_shown?.length && (
                <div className="flex justify-center pt-2">
                  <button
                    onClick={() => generateBridges(index)}
                    disabled={generatingForIndex === index}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingForIndex === index ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        <span>Generate Bridge Options</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* AI Options */}
              {bridge.ai_options_shown?.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-purple-900 mb-3">
                    AI-Generated Options (select one or write your own below):
                  </p>
                  <div className="space-y-2">
                    {bridge.ai_options_shown.map((option, optionIndex) => (
                      <button
                        key={optionIndex}
                        onClick={() => selectAIOption(index, optionIndex)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition ${
                          bridge.student_selected === optionIndex && !bridge.student_edited
                            ? 'border-purple-500 bg-purple-100'
                            : 'border-gray-300 bg-white hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-start space-x-2">
                          <span className="flex-shrink-0 text-xs font-bold text-purple-700">
                            {optionIndex + 1}
                          </span>
                          <p className="text-sm text-gray-800 flex-1">{option}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => generateBridges(index)}
                    disabled={generatingForIndex === index}
                    className="mt-3 text-xs text-purple-700 hover:text-purple-900 font-medium"
                  >
                    ↻ Regenerate options
                  </button>
                </div>
              )}

              {/* Field 3: Bridge Sentence (Final) */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Your Bridge Sentence
                  {bridge.student_edited && (
                    <span className="ml-2 text-xs text-amber-600">✏️ Edited by you</span>
                  )}
                </label>
                <textarea
                  value={bridge.bridge_sentence || ''}
                  onChange={(e) => updateBridge(index, 'bridge_sentence', e.target.value)}
                  placeholder="Write or edit your bridge sentence here (1-2 sentences showing HOW you'll engage)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[80px]"
                  style={{ height: 'auto', minHeight: '80px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Empty State */}
        {(!data.rows || data.rows.length === 0) && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-sm text-gray-600 mb-4">No bridges created yet</p>
            <button
              onClick={addBridge}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              <Plus className="w-5 h-5" />
              <span>Create Your First Bridge</span>
            </button>
          </div>
        )}

        {/* Minimum requirement note */}
        {data.rows && data.rows.length > 0 && data.rows.length < 2 && (
          <p className="text-sm text-amber-600 text-center">
            ⚠ Add at least {2 - data.rows.length} more bridge(s) to complete this section
          </p>
        )}
      </div>

      {/* Example/Tip Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Pro Tip</h4>
        <p className="text-sm text-blue-800">
          Great bridge sentences answer "how will I engage?" rather than "what exists?".
          Instead of "I want to take Professor Smith's class," try "Professor Smith's research on
          X-ray crystallography would let me test whether my protein-folding hypothesis holds at
          atomic scale."
        </p>
      </div>
    </div>
  );
}
